// Player detail bottom sheet: headline stat tiles, streak badges, OPS-by-game
// sparkline, per-game log, and (for pitchers) command + forecast context.

import React from 'react';
import { GS } from '../../lib/designTokens';
import { fmtStat } from '../../lib/stat-format';
import { Sheet } from './Sheet';
import { Card, JerseyChip, Sparkline, ScoreBadge } from './ui';
import { commandScore, controlGradeTo99, forecastInnings, PitchCapRules } from '../../lib/pitching-plan';
import type { SeasonData } from './useSeasonStats';

const StatTile = ({ v, l }: { v: string; l: string }) => (
  <div className="rounded-xl border px-1 py-2.5 text-center" style={{ background: GS.panel, borderColor: GS.line }}>
    <div className="text-[15px] font-black" style={{ color: v === '—' ? GS.ink3 : GS.ink }}>{v}</div>
    <div className="text-[8px] font-extrabold uppercase mt-0.5" style={{ color: GS.ink3, letterSpacing: '0.05em' }}>{l}</div>
  </div>
);

export const PlayerDetailSheet = ({ playerId, data, capRules, isHeadCoach, onClose }: {
  playerId: string | null;
  data: SeasonData;
  capRules: PitchCapRules | null;
  isHeadCoach?: boolean;
  onClose: () => void;
}) => {
  const s: any = playerId ? data.stats[playerId] : null;
  const b = s?.batting;
  const pit = s?.pitching;
  const player = s?.player;

  const group = playerId ? data.signalsByPlayer.find(g => g.playerId === playerId) : null;
  const tone: 'default' | 'hot' | 'cold' = group
    ? (group.signals.some(sig => sig.direction === 'hot') ? 'hot' : 'cold')
    : 'default';

  // Per-game log rows + OPS sparkline from game.stats
  const log = playerId
    ? data.sortedGames
        .map(g => ({ g, gs: g.stats?.[playerId] }))
        .filter(({ gs }) => gs && ((gs.pa || 0) > 0 || (gs.ab || 0) > 0 || (gs.ip || 0) > 0))
    : [];
  const opsSeries = log.map(({ gs }) => gs.ops).filter((v: any) => v != null) as number[];
  const missingStory = b && b.pa > 0 && b.qab == null;

  const cmd = pit ? commandScore(pit) : null;
  // Control is a nested scouting attribute: attributes.pitching.control (1–5)
  const controlGrade = player?.attributes?.pitching?.control ?? player?.attributes?.control ?? null;
  const control99 = controlGradeTo99(controlGrade);
  const forecast = pit ? forecastInnings(pit.pIp, capRules) : null;

  return (
    <Sheet
      open={!!playerId && !!s}
      onClose={onClose}
      title={player ? (
        <span className="flex items-center gap-2.5">
          <JerseyChip number={player?.number ?? ''} tone={tone} />
          <span>{player?.name}</span>
        </span>
      ) : undefined}
      sub={player?.positions?.length ? player.positions.join(' · ') : undefined}
    >
      {s && (
        <div className="space-y-3">
          {/* Headline tiles */}
          <div className="grid grid-cols-4 gap-2">
            <StatTile v={fmtStat(b.avg, 'avg3')} l="AVG" />
            <StatTile v={fmtStat(b.obp, 'avg3')} l="OBP" />
            <StatTile v={fmtStat(b.ops, 'avg3')} l="OPS" />
            <StatTile v={fmtStat(b.qabPct, 'pct')} l="QAB%" />
            <StatTile v={b.sb > 0 ? fmtStat(b.sb, 'count') : '—'} l="SB" />
            <StatTile v={fmtStat(b.hhb, 'count')} l="HHB" />
            <StatTile v={fmtStat(b.baRisp, 'avg3')} l="RISP" />
            <StatTile v={fmtStat(b.kLooking, 'count')} l="K-L" />
          </div>

          {/* Streak badges */}
          {group && group.signals.length > 0 && (
            <div
              className="rounded-2xl border px-3.5 py-2.5 text-[11px] font-semibold leading-relaxed"
              style={tone === 'hot'
                ? { background: GS.greenSoft, borderColor: GS.green, color: GS.ink }
                : { background: '#E4EEF4', borderColor: '#B9D4E4', color: '#2C5A77' }}
            >
              {group.signals.slice(0, 3).map((sig, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  <b>{sig.direction === 'hot' ? '🔥' : '❄️'} {sig.label}</b>
                  {sig.detail ? ` (${sig.detail})` : ''}
                </span>
              ))}
            </div>
          )}

          {/* Sparkline */}
          {opsSeries.length >= 2 && (
            <Card className="px-4 pt-3 pb-1.5">
              <div className="flex justify-between items-baseline">
                <span className="text-[9px] font-black uppercase" style={{ color: GS.ink3, letterSpacing: '0.1em' }}>OPS by game</span>
                <span className="text-[11px] font-black" style={{ color: GS.greenInk }}>{fmtStat(b.ops, 'avg3')} season</span>
              </div>
              <Sparkline values={opsSeries} />
            </Card>
          )}

          {/* Pitcher context */}
          {pit && (pit.ip > 0 || pit.pitches > 0) && (
            <Card className="px-4 py-3">
              <div className="flex items-center gap-3">
                {cmd ? <ScoreBadge score={cmd.score} label="CMD" /> : null}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-extrabold" style={{ color: GS.ink }}>
                    {fmtStat(pit.sPct, 'pct')} strikes · {fmtStat(pit.fpsPct, 'pct')} first-pitch · {fmtStat(pit.bbInn, 'ratio1')} BB/inn
                  </p>
                  <p className="text-[10px] font-semibold mt-0.5" style={{ color: GS.ink2 }}>
                    {forecast?.forecastIP != null
                      ? <>≈ {fmtStat(forecast.forecastIP, 'ip')} IP available per outing{forecast.capPitches ? ` on a ${forecast.capPitches}-pitch cap` : ''}</>
                      : `${fmtStat(pit.ipTrue ?? pit.ip, 'ip')} IP this season`}
                  </p>
                </div>
              </div>
              {isHeadCoach && cmd && control99 != null && Math.abs(cmd.score - control99) >= 15 && (
                <p className="text-[10px] font-semibold mt-2 pt-2 leading-relaxed" style={{ color: GS.ink2, borderTop: `1px solid ${GS.line}` }}>
                  Command {cmd.score} vs your Control grade ({controlGrade}/5 ≈ {control99}):{' '}
                  <b style={{ color: GS.ink }}>{cmd.score > control99 ? 'playing above your grade' : 'playing below your grade'}</b>. Worth a look at the next Roster review.
                </p>
              )}
            </Card>
          )}

          {/* Game log */}
          {log.length > 0 && (
            <Card>
              <div className="grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr] px-4 py-2 text-[8px] font-black uppercase" style={{ color: GS.ink3, letterSpacing: '0.07em', borderBottom: `1px solid ${GS.line}` }}>
                <span>Game</span><span className="text-right">AB</span><span className="text-right">H</span><span className="text-right">BB</span><span className="text-right">QAB</span>
              </div>
              {[...log].reverse().slice(0, 8).map(({ g, gs }, i) => (
                <div key={g.id || i} className="grid grid-cols-[1.6fr_1fr_1fr_1fr_1fr] px-4 py-2 text-[11px] font-bold" style={{ color: GS.ink, borderTop: i > 0 ? `1px solid ${GS.line}` : undefined }}>
                  <span className="truncate">
                    <span className="font-extrabold">{g.opponent || '?'}</span>{' '}
                    <span className="text-[9px] font-bold" style={{ color: GS.ink3 }}>
                      {g.date ? new Date(g.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </span>
                  <span className="text-right">{fmtStat(gs.ab, 'count')}</span>
                  <span className="text-right">{fmtStat(gs.h, 'count')}</span>
                  <span className="text-right">{fmtStat(gs.bb, 'count')}</span>
                  <span className="text-right">{fmtStat(gs.qab, 'count')}</span>
                </div>
              ))}
            </Card>
          )}

          {missingStory && (
            <p className="text-[10px] font-semibold leading-relaxed px-1" style={{ color: GS.ink2 }}>
              Some at-bat quality stats show “—” because older imports don't include them.{' '}
              <b style={{ color: GS.greenInk }}>Re-import your latest GameChanger CSV</b>. One import backfills the season.
            </p>
          )}
        </div>
      )}
    </Sheet>
  );
};
